import { dateRangeFromISO, getDateRange } from "@app/lib/date-range";
import {
  bumpPerspectivesVersion,
  getPerspectiveId,
  setPerspectiveId,
} from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useToast } from "@app/components/toast";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { categorizePage_Query } from "@app/queries/categorizePage_Query.graphql";
import type { categorizePage_CategorizeStakeholderMutation } from "@app/queries/categorizePage_CategorizeStakeholderMutation.graphql";
import type { categorizePage_StakeholderInternalSetMutation } from "@app/queries/categorizePage_StakeholderInternalSetMutation.graphql";
import type { categorizePage_PerspectivesImportMutation } from "@app/queries/categorizePage_PerspectivesImportMutation.graphql";
import type { categorizePage_RenamePerspectiveMutation } from "@app/queries/categorizePage_RenamePerspectiveMutation.graphql";
import type { categorizePage_DeletePerspectiveMutation } from "@app/queries/categorizePage_DeletePerspectiveMutation.graphql";

import CategoryDetailPanel from "./category-detail-panel";
import CategoryTree from "./category-tree";
import SplitEditor from "./split-editor";
import StakeholderDetailPanel from "./stakeholder-detail-panel";
import type { Category } from "./types";
import type { PageData, PoolEntry } from "./types";

export const categorizeQuery = graphql`
  query categorizePage_Query($perspectiveId: ID, $from: String) {
    perspectivesExport
    categorizePage(perspectiveId: $perspectiveId, from: $from) {
      perspective {
        id
        name
      }
      categories {
        id
        name
        color
        rank
        pnlLine
        parent {
          id
        }
      }
      stakeholders {
        stakeholder {
          id
          name
          internal
        }
        totalSpend
        allocations {
          id
          weight
          sliceType
          amountCents
          ordinal
          kind
          category {
            id
            name
            color
          }
        }
      }
    }
  }
`;

const categorizeStakeholderMutation = graphql`
  mutation categorizePage_CategorizeStakeholderMutation(
    $input: CategorizeStakeholderInputObject!
  ) {
    categorizeStakeholder(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const perspectivesImportMutation = graphql`
  mutation categorizePage_PerspectivesImportMutation(
    $input: PerspectivesImportInputObject!
  ) {
    perspectivesImport(input: $input) {
      perspectivesImported
      categoriesImported
      categorizationsImported
      errors {
        message
      }
    }
  }
`;

const stakeholderInternalSetMutation = graphql`
  mutation categorizePage_StakeholderInternalSetMutation(
    $input: StakeholderInternalSetInputObject!
  ) {
    stakeholderInternalSet(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const renamePerspectiveMutation = graphql`
  mutation categorizePage_RenamePerspectiveMutation(
    $input: RenamePerspectiveInputObject!
  ) {
    renamePerspective(input: $input) {
      perspective {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const deletePerspectiveMutation = graphql`
  mutation categorizePage_DeletePerspectiveMutation(
    $input: DeletePerspectiveInputObject!
  ) {
    deletePerspective(input: $input) {
      perspective {
        id
      }
      errors {
        message
      }
    }
  }
`;

export const STAKEHOLDER_DRAG_TYPE = "application/x-cxcash-stakeholders";

type Filter = "ALL" | "UNCATEGORIZED" | "CATEGORIZED" | "INTERNAL";

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export default function CategorizePage({
  queryRef,
}: {
  queryRef: PreloadedQuery<categorizePage_Query>;
}) {
  const preloaded = usePreloadedQuery(categorizeQuery, queryRef);
  const router = useRouter();
  const notify = useToast();

  // Mutations must not re-run the route loader (that unmounts the page and
  // resets scroll/selection). Render from state seeded by the loader; after
  // each mutation, silently refetch and swap the data in place.
  const [data, setData] = useState<PageData>(preloaded.categorizePage);
  useEffect(() => setData(preloaded.categorizePage), [preloaded]);

  // The downloadable backup string, kept current as categorizations change.
  const [exportJson, setExportJson] = useState(preloaded.perspectivesExport);
  useEffect(() => setExportJson(preloaded.perspectivesExport), [preloaded]);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("UNCATEGORIZED");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // Stakeholder ids with an in-flight internal-mark round-trip, so the row's
  // button can show a spinner + fade instead of appearing to do nothing.
  const [internalBusy, setInternalBusy] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [splitFor, setSplitFor] = useState<PoolEntry | null>(null);
  const [detailFor, setDetailFor] = useState<PoolEntry | null>(null);
  const [detailCategory, setDetailCategory] = useState<Category | null>(null);

  const refreshInPlace = () => {
    fetchQuery<categorizePage_Query>(environment, categorizeQuery, {
      perspectiveId: getPerspectiveId() ?? undefined,
      from: dateRangeFromISO(getDateRange()) ?? undefined,
    })
      .toPromise()
      .then((fresh) => {
        if (!fresh) return;
        setData(fresh.categorizePage);
        setExportJson(fresh.perspectivesExport);
      });
  };

  const perspective = data.perspective;

  const applyAllocations = (
    stakeholderIds: readonly string[],
    allocations: {
      categoryId: string;
      weight?: number;
      sliceType?: string;
      amountCents?: number;
      ordinal?: number;
    }[]
  ) => {
    let remaining = stakeholderIds.length;
    stakeholderIds.forEach((stakeholderId) => {
      commitMutation<categorizePage_CategorizeStakeholderMutation>(
        environment,
        {
          mutation: categorizeStakeholderMutation,
          variables: {
            input: {
              stakeholderId,
              perspectiveId: perspective.id,
              allocations,
            },
          },
          onCompleted: () => {
            remaining -= 1;
            if (remaining === 0) refreshInPlace();
          },
          onError: (error) => window.alert(error.message),
        }
      );
    });
    setSelected(new Set());
  };

  const exportBackup = () => {
    const blob = new Blob([exportJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "qbo-sync-perspectives.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importBackup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      file.text().then((json) => {
        commitMutation<categorizePage_PerspectivesImportMutation>(environment, {
          mutation: perspectivesImportMutation,
          variables: { input: { json } },
          onCompleted: (response, errors) => {
            if (errors?.length) {
              window.alert(errors[0].message);
              return;
            }
            const payload = response.perspectivesImport;
            window.alert(
              `Imported ${payload?.perspectivesImported ?? 0} perspectives, ` +
                `${payload?.categoriesImported ?? 0} categories, ` +
                `${payload?.categorizationsImported ?? 0} categorizations.`
            );
            router.invalidate();
          },
          onError: (error) => window.alert(error.message),
        });
      });
    }
    event.target.value = "";
  };

  const setInternal = (stakeholderId: string, internal: boolean) => {
    setInternalBusy((prev) => new Set(prev).add(stakeholderId));
    const clearBusy = () =>
      setInternalBusy((prev) => {
        const next = new Set(prev);
        next.delete(stakeholderId);
        return next;
      });
    commitMutation<categorizePage_StakeholderInternalSetMutation>(environment, {
      mutation: stakeholderInternalSetMutation,
      variables: { input: { stakeholderId, internal } },
      onCompleted: (_response, errors) => {
        clearBusy();
        if (errors?.length) {
          notify({ message: errors[0].message, kind: "error" });
          return;
        }
        notify({
          message: internal
            ? "Marked internal — excluded from cost/revenue."
            : "Restored to a normal counterparty.",
          kind: "success",
        });
        refreshInPlace();
      },
      onError: (error) => {
        clearBusy();
        notify({ message: error.message, kind: "error" });
      },
    });
  };

  const renamePerspective = () => {
    const name = window.prompt("Rename perspective:", perspective.name);
    if (!name?.trim() || name.trim() === perspective.name) return;

    commitMutation<categorizePage_RenamePerspectiveMutation>(environment, {
      mutation: renamePerspectiveMutation,
      variables: {
        input: { perspectiveId: perspective.id, name: name.trim() },
      },
      onCompleted: refreshInPlace,
      onError: (error) => window.alert(error.message),
    });
  };

  const deletePerspective = () => {
    if (
      !window.confirm(
        `Delete perspective "${perspective.name}"? Its category tree and all categorizations made through it are removed permanently.`
      )
    ) {
      return;
    }

    commitMutation<categorizePage_DeletePerspectiveMutation>(environment, {
      mutation: deletePerspectiveMutation,
      variables: { input: { perspectiveId: perspective.id } },
      onCompleted: (_response, errors) => {
        if (errors?.length) {
          window.alert(errors[0].message);
          return;
        }
        setPerspectiveId(null);
        bumpPerspectivesVersion();
        router.invalidate();
      },
      onError: (error) => window.alert(error.message),
    });
  };

  const pool = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return data.stakeholders.filter((entry) => {
      if (needle && !entry.stakeholder.name.toLowerCase().includes(needle))
        return false;
      // Internal counterparties are treasury, not cost/revenue — they only
      // surface under the Internal filter, never in the working views.
      if (filter === "INTERNAL") return entry.stakeholder.internal;
      if (entry.stakeholder.internal) return false;
      if (filter === "UNCATEGORIZED") return entry.allocations.length === 0;
      if (filter === "CATEGORIZED") return entry.allocations.length > 0;
      return true;
    });
  }, [data.stakeholders, search, filter]);

  // Internal counterparties aren't part of the cost/revenue picture, so they
  // don't count toward the categorization progress shown in the header.
  const costStakeholders = useMemo(
    () => data.stakeholders.filter((entry) => !entry.stakeholder.internal),
    [data.stakeholders]
  );
  const uncategorizedCount = useMemo(
    () =>
      costStakeholders.filter((entry) => entry.allocations.length === 0).length,
    [costStakeholders]
  );

  const toggleSelected = (id: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const dragIdsFor = (entry: PoolEntry): string[] =>
    selected.has(entry.stakeholder.id) ? [...selected] : [entry.stakeholder.id];

  // Dropping a counterparty back onto the pool clears its allocations — the way
  // to undo a miscategorization without picking a different category. The ref
  // suppresses the uncategorize hint for drags that start in the pool (a normal
  // pool→category assignment), so only node→pool drags light it up.
  const [poolDropActive, setPoolDropActive] = useState(false);
  const draggingFromPool = useRef(false);

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-bold">Categorize</h1>
        <span className="text-sm text-gray-500">
          {perspective.name} — {uncategorizedCount} of {costStakeholders.length}{" "}
          counterparties uncategorized
        </span>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={exportBackup}
            title="Download all perspectives, category trees and categorizations as JSON"
            className="px-3 py-1.5 rounded border hover:bg-gray-50"
          >
            Export backup
          </button>
          <label
            title="Restore perspectives and categorizations from a previously exported backup"
            className="px-3 py-1.5 rounded border hover:bg-gray-50 cursor-pointer"
          >
            Import backup
            <input
              type="file"
              accept="application/json,.json"
              onChange={importBackup}
              className="hidden"
            />
          </label>
          <button
            type="button"
            onClick={renamePerspective}
            className="text-gray-500 hover:text-gray-900"
          >
            Rename perspective
          </button>
          <button
            type="button"
            onClick={deletePerspective}
            className="text-red-400 hover:text-red-600"
          >
            Delete perspective
          </button>
        </div>
      </div>

      <div className="flex gap-6 items-start">
        {/* Stakeholder pool */}
        <div
          className={`w-2/5 bg-white border rounded-lg transition-colors ${
            poolDropActive
              ? "border-emerald-400 ring-2 ring-emerald-300"
              : "border-gray-200"
          }`}
          onDragOver={(event) => {
            if (event.dataTransfer.types.includes(STAKEHOLDER_DRAG_TYPE)) {
              event.preventDefault();
              if (!draggingFromPool.current) setPoolDropActive(true);
            }
          }}
          onDragLeave={() => setPoolDropActive(false)}
          onDrop={(event) => {
            const payload = event.dataTransfer.getData(STAKEHOLDER_DRAG_TYPE);
            if (!payload) return;
            event.preventDefault();
            setPoolDropActive(false);
            applyAllocations(JSON.parse(payload) as string[], []);
          }}
        >
          <div className="p-3 border-b border-gray-100 flex items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search counterparties…"
              className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5"
            />
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value as Filter)}
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="UNCATEGORIZED">Uncategorized</option>
              <option value="CATEGORIZED">Categorized</option>
              <option value="ALL">All</option>
              <option value="INTERNAL">Internal (treasury)</option>
            </select>
          </div>

          {poolDropActive && (
            <div className="px-3 py-2 text-xs bg-emerald-50 border-b border-emerald-100 text-emerald-800">
              Drop here to remove this counterparty from its category
            </div>
          )}

          {selected.size > 0 && (
            <div className="px-3 py-2 text-xs bg-amber-50 border-b border-amber-100 text-amber-800 flex items-center gap-2">
              {selected.size} selected — drag any selected row onto a category
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="ml-auto underline hover:no-underline"
              >
                Clear
              </button>
            </div>
          )}

          <ul className="divide-y divide-gray-50 max-h-[70vh] overflow-y-auto">
            {pool.length === 0 && (
              <li className="p-4 text-sm text-gray-400">
                {filter === "UNCATEGORIZED"
                  ? "Everything here is categorized. Switch the filter to revisit past decisions."
                  : "No counterparties match."}
              </li>
            )}
            {pool.map((entry) => {
              const internalPending = internalBusy.has(entry.stakeholder.id);
              return (
              <li
                key={entry.stakeholder.id}
                draggable
                onDragStart={(event) => {
                  draggingFromPool.current = true;
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData(
                    STAKEHOLDER_DRAG_TYPE,
                    JSON.stringify(dragIdsFor(entry))
                  );
                }}
                onDragEnd={() => {
                  draggingFromPool.current = false;
                }}
                className={`flex items-center gap-2 px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-50 transition-opacity ${
                  selected.has(entry.stakeholder.id) ? "bg-amber-50" : ""
                } ${internalPending ? "opacity-60" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(entry.stakeholder.id)}
                  onChange={() => toggleSelected(entry.stakeholder.id)}
                  className="shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setDetailFor(entry)}
                    title="Open details"
                    className="block max-w-full text-left text-sm text-gray-900 truncate hover:underline"
                  >
                    {entry.stakeholder.name}
                  </button>
                  {entry.allocations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {entry.allocations.map((allocation) => (
                        <button
                          key={allocation.id}
                          type="button"
                          onClick={() => setSplitFor(entry)}
                          title="Edit allocation"
                          className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-600 hover:border-gray-400"
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: allocation.category.color,
                            }}
                          />
                          {allocation.category.name}
                          {allocation.weight < 1 &&
                            ` ${Math.round(allocation.weight * 100)}%`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className="shrink-0 text-xs text-gray-400 tabular-nums">
                  {money(entry.totalSpend)}
                </span>
                {entry.stakeholder.internal ? (
                  <button
                    type="button"
                    disabled={internalPending}
                    onClick={() => setInternal(entry.stakeholder.id, false)}
                    title="Treat as a normal counterparty again (back into cost/revenue)"
                    className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-40 disabled:hover:text-gray-400"
                  >
                    {internalPending && (
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                    )}
                    {internalPending ? "unmarking…" : "unmark"}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setSplitFor(entry)}
                      title="Split across categories"
                      className="shrink-0 text-xs text-gray-400 hover:text-gray-700"
                    >
                      split
                    </button>
                    <button
                      type="button"
                      disabled={internalPending}
                      onClick={() => setInternal(entry.stakeholder.id, true)}
                      title="Mark internal — an own account or pass-through, excluded from cost/revenue"
                      className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-40 disabled:hover:text-gray-400"
                    >
                      {internalPending && (
                        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                      )}
                      {internalPending ? "marking…" : "internal"}
                    </button>
                  </>
                )}
              </li>
              );
            })}
          </ul>
        </div>

        {/* Category tree */}
        <div className="flex-1">
          <CategoryTree
            perspectiveId={perspective.id}
            categories={data.categories}
            stakeholders={costStakeholders}
            onChanged={refreshInPlace}
            onDropStakeholders={(categoryId, stakeholderIds) =>
              applyAllocations(stakeholderIds, [{ categoryId, weight: 1 }])
            }
            onOpenStakeholder={setDetailFor}
            onOpenCategory={setDetailCategory}
          />
        </div>
      </div>

      {splitFor && (
        <SplitEditor
          entry={splitFor}
          categories={data.categories}
          onClose={() => setSplitFor(null)}
          onSave={(allocations) => {
            applyAllocations([splitFor.stakeholder.id], allocations);
            setSplitFor(null);
          }}
        />
      )}

      {detailFor && (
        <StakeholderDetailPanel
          entry={detailFor}
          perspectiveId={perspective.id}
          onClose={() => setDetailFor(null)}
          onOpenCategory={(categoryId) => {
            const target = data.categories.find((c) => c.id === categoryId);
            if (target) {
              setDetailFor(null);
              setDetailCategory(target);
            }
          }}
        />
      )}

      {detailCategory && (
        <CategoryDetailPanel
          category={detailCategory}
          perspectiveId={perspective.id}
          onClose={() => setDetailCategory(null)}
          onOpenStakeholder={(stakeholderId) => {
            const entry = data.stakeholders.find(
              (s) => s.stakeholder.id === stakeholderId
            );
            if (entry) {
              setDetailCategory(null);
              setDetailFor(entry);
            }
          }}
        />
      )}
    </div>
  );
}
